import { alpha, createTheme } from '@mui/material/styles';

/** Brand yellow (optional accents only; not global palette.primary in dark mode). */
export const BRAND_PRIMARY_MAIN = '#ffea00';
export const BRAND_PRIMARY_CONTRAST = '#0f1419';
/** Deep brand blue (light surfaces + references). */
export const BRAND_SECONDARY_MAIN = '#00228f';

const LIGHT_PRIMARY = {
  main: BRAND_SECONDARY_MAIN,
  light: '#33499f',
  dark: '#001563',
  contrastText: '#ffffff',
};

const LIGHT_SECONDARY = {
  main: '#546e7a',
  light: '#78909c',
  dark: '#37474f',
  contrastText: '#ffffff',
};

/** Material Design default blue (`palette.primary` in stock MUI), used as semantic primary on dark surfaces. */
const DARK_PRIMARY = {
  main: '#7fbaf5',
  light: '#42a5f5',
  dark: '#1565c0',
  contrastText: '#ffffff',
};

const DARK_SECONDARY = {
  main: '#64748b',
  light: '#94a3b8',
  dark: '#475569',
  contrastText: '#ffffff',
};

/**
 * Application theme for the management dashboard. Pass resolved palette mode ('light' | 'dark').
 *
 * Light mode: FunZone deep blue primary (readable on white). Dark mode: standard MUI Material blue primary; FunZone yellow stays export-only for deliberate accents.
 */
export function createManagementTheme(mode) {
  const isLight = mode === 'light';

  return createTheme({
    palette: {
      mode,
      primary: isLight ? LIGHT_PRIMARY : DARK_PRIMARY,
      secondary: isLight ? LIGHT_SECONDARY : DARK_SECONDARY,
    },
    components: {
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            textTransform: 'none',
          },
          containedPrimary: ({ theme }) =>
            theme.palette.mode === 'light'
              ? {
                  backgroundColor: BRAND_SECONDARY_MAIN,
                  color: '#ffffff',
                  '&:hover': { backgroundColor: '#001563' },
                  '&:active': { backgroundColor: '#001244' },
                  '&.Mui-disabled': {
                    backgroundColor: alpha(BRAND_SECONDARY_MAIN, 0.38),
                    color: alpha('#ffffff', 0.65),
                  },
                }
              : {
                  '&:hover': {
                    backgroundColor: theme.palette.primary.dark,
                  },
                },
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          body: ({ theme }) => ({
            color: theme.palette.text.primary,
            backgroundColor: theme.palette.background.default,
          }),
        },
      },
      MuiAppBar: {
        defaultProps: {
          enableColorOnDark: true,
        },
        styleOverrides: {
          root: ({ theme: t }) =>
            t.palette.mode === 'light'
              ? {
                  color: t.palette.text.primary,
                }
              : {},
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: ({ theme: t }) => ({
            backgroundImage: 'none',
            backgroundColor: t.palette.background.paper,
          }),
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: ({ theme: t }) =>
            t.palette.mode === 'light'
              ? {
                  backgroundImage: 'none',
                }
              : {},
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: ({ theme }) => ({
            color: theme.palette.text.primary,
          }),
        },
      },
      MuiCard: {
        styleOverrides: {
          root: ({ theme }) => ({
            color: theme.palette.text.primary,
          }),
        },
      },
      MuiAccordion: {
        styleOverrides: {
          root: ({ theme }) => ({
            color: theme.palette.text.primary,
          }),
        },
      },
    },
  });
}
